import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Store, MapPin, Loader2, Camera } from "lucide-react";
import { useI18n } from "@/lib/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  useMarketNearby,
  useCreateListing,
  DRAG_MIME,
  parseDropPayload,
} from "@/lib/market";
import { useInventory } from "@/lib/inventory";
import CreateListingModal from "@/components/CreateListingModal";
import ListingDetailModal from "@/components/ListingDetailModal";
import MatchBidList from "@/components/MatchBidList";

const TAB_MARKET = "matchmarket";
const TAB_BID = "matchbid";

// Phase 22a/b — MatchMarket + MatchBid dual-tab shell.
// Tab labels ("MatchMarket" / "MatchBid") are proper nouns — NOT translated.

export default function MarketPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [tab, setTab] = useState(TAB_MARKET);
  const [dragActive, setDragActive] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("fixed"); // "fixed" | "auction"
  const [detailId, setDetailId] = useState(null);
  const navigate = useNavigate();
  const inv = useInventory();
  const { data, isLoading } = useMarketNearby(tab === TAB_MARKET);

  // Global drag-over listener: whenever the user drags a sticker chip
  // (payload MIME `application/x-figumatch-listing`), light up the drop-zone.
  useEffect(() => {
    const dragEnter = (e) => {
      const types = e.dataTransfer?.types;
      if (!types) return;
      if (Array.from(types).includes(DRAG_MIME)) {
        setDragActive(true);
      }
    };
    const dragLeaveWindow = (e) => {
      // Only clear when the pointer actually leaves the browser viewport.
      if (
        e.clientX <= 0 ||
        e.clientY <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY >= window.innerHeight
      ) {
        setDragActive(false);
      }
    };
    const dragEnd = () => setDragActive(false);
    document.addEventListener("dragenter", dragEnter);
    document.addEventListener("dragleave", dragLeaveWindow);
    document.addEventListener("dragend", dragEnd);
    document.addEventListener("drop", dragEnd);
    // Phase 22a — touch-drop bus: mobile StickerChip touch handler fires
    // a custom event when a chip is released over the drop-zone.
    const touchDrop = (ev) => {
      const detail = ev.detail;
      if (!detail || !Number.isFinite(detail.number)) return;
      setPrefill({ number: detail.number, grade: detail.grade ?? null });
      setModalMode("fixed");
      setModalOpen(true);
      setDragActive(false);
    };
    document.addEventListener("figumatch:listing-touch-drop", touchDrop);
    return () => {
      document.removeEventListener("dragenter", dragEnter);
      document.removeEventListener("dragleave", dragLeaveWindow);
      document.removeEventListener("dragend", dragEnd);
      document.removeEventListener("drop", dragEnd);
      document.removeEventListener(
        "figumatch:listing-touch-drop",
        touchDrop
      );
    };
  }, []);

  const listings = data?.listings || [];
  const hasLocation = !!user?.location || !!inv?.data;

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const payload = parseDropPayload(e.dataTransfer);
    if (!payload) return;
    setPrefill({ number: payload.number, grade: payload.grade ?? null });
    setModalMode("fixed");
    setModalOpen(true);
  }, []);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const openCreateManually = useCallback(() => {
    setPrefill(null);
    setModalMode("fixed");
    setModalOpen(true);
  }, []);

  const openCreateAuction = useCallback(() => {
    setPrefill(null);
    setModalMode("auction");
    setModalOpen(true);
  }, []);

  return (
    <div data-testid="market-page" className="min-h-full">
      <header className="mb-6">
        <div className="flex items-center gap-3">
          <Store className="h-6 w-6 text-[#E5B83A]" />
          <h1 className="font-display text-2xl font-semibold text-white">
            MatchMarket
          </h1>
        </div>
        <p className="mt-1 text-xs text-neutral-400 font-body">
          {t(
            "market.subtitle",
            "Buy and sell duplicates with nearby collectors — FiguTokens only."
          )}
        </p>
      </header>

      {/* ── Tab strip ───────────────────────────────────────────────── */}
      <div
        role="tablist"
        aria-label="Market mode"
        data-testid="market-tablist"
        className="flex w-full border-b border-white/10 mb-6"
      >
        <button
          role="tab"
          type="button"
          data-testid="tab-matchmarket"
          aria-selected={tab === TAB_MARKET}
          aria-controls="tab-panel-matchmarket"
          onClick={() => setTab(TAB_MARKET)}
          className={
            "flex-1 min-h-[44px] px-4 pb-3 pt-2 text-sm font-medium tracking-wide transition-colors " +
            (tab === TAB_MARKET
              ? "text-white border-b-2 border-[#E5B83A]"
              : "text-[#A1A1AA] hover:text-white")
          }
        >
          MatchMarket
        </button>
        <button
          role="tab"
          type="button"
          data-testid="tab-matchbid"
          aria-selected={tab === TAB_BID}
          aria-controls="tab-panel-matchbid"
          onClick={() => setTab(TAB_BID)}
          className={
            "flex-1 min-h-[44px] px-4 pb-3 pt-2 text-sm font-medium tracking-wide transition-colors " +
            (tab === TAB_BID
              ? "text-white border-b-2 border-[#E5B83A]"
              : "text-[#A1A1AA] hover:text-white")
          }
        >
          MatchBid
        </button>
      </div>

      {tab === TAB_MARKET ? (
        <section
          id="tab-panel-matchmarket"
          role="tabpanel"
          aria-labelledby="tab-matchmarket"
          data-testid="tab-panel-matchmarket"
          className="space-y-4"
        >
          {/* Drop-zone */}
          <div
            data-testid="market-drop-zone"
            data-drag-active={dragActive ? "true" : "false"}
            onDragOver={onDragOver}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            className={
              "rounded-lg border-2 border-dashed p-5 text-center transition-colors " +
              (dragActive
                ? "border-[#FFD700] bg-[#FFD700]/10"
                : "border-white/15 bg-black/30")
            }
          >
            <p
              data-testid="market-drop-label"
              className="text-sm text-[#FFD700] font-body"
            >
              {t(
                "market.drop_here_to_list",
                "Drop here to list on MatchMarket"
              )}
            </p>
            <p className="mt-1 text-[11px] text-[#A1A1AA] font-body">
              {t(
                "market.drop_help",
                "Drag any duplicate sticker chip here — or create one manually."
              )}
            </p>
            <button
              type="button"
              onClick={openCreateManually}
              data-testid="market-create-manual"
              className="mt-3 h-9 px-4 rounded-md bg-transparent border border-[#E5B83A]/60 text-[#E5B83A] font-medium text-xs hover:bg-[#E5B83A]/10 transition-colors"
            >
              {t("market.create_manual", "Create listing manually")}
            </button>
          </div>

          {isLoading ? (
            <div
              data-testid="market-loading"
              className="flex items-center justify-center py-16 text-white/60"
            >
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : listings.length === 0 ? (
            <div
              data-testid="market-empty"
              className="rounded-lg border border-white/10 bg-black/40 px-6 py-10 text-center text-sm text-[#A1A1AA] font-body"
            >
              {t(
                "market.empty_no_listings",
                "No listings within 5km yet. Be the first to sell."
              )}
            </div>
          ) : (
            <div
              data-testid="market-listings-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {listings.map((l) => (
                <ListingCard
                  key={l.id}
                  listing={l}
                  onClick={() => setDetailId(l.id)}
                />
              ))}
            </div>
          )}
        </section>
      ) : (
        <section
          id="tab-panel-matchbid"
          role="tabpanel"
          aria-labelledby="tab-matchbid"
          data-testid="tab-panel-matchbid"
        >
          <MatchBidList onCreateAuction={openCreateAuction} />
        </section>
      )}

      <CreateListingModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        prefill={prefill}
        initialMode={modalMode}
      />
      <ListingDetailModal
        id={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
}

function ListingCard({ listing, onClick }) {
  const { t } = useI18n();
  const primaryPhoto =
    listing.photo_urls && listing.photo_urls.length > 0
      ? listing.photo_urls[0]
      : null;
  const distance = Number.isFinite(listing.distance_meters)
    ? `${Math.round(listing.distance_meters)}m`
    : null;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`listing-card-${listing.id}`}
      className="text-left rounded-lg border border-[#FFD700]/25 bg-[#0A0A0A] overflow-hidden hover:border-[#FFD700]/60 transition-colors group"
    >
      <div className="aspect-[4/3] bg-black flex items-center justify-center overflow-hidden">
        {primaryPhoto ? (
          <img
            src={primaryPhoto}
            alt={`Sticker #${listing.number}`}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-white/40 font-mono">
            <Camera className="h-6 w-6" />
            <span className="text-xs">#{listing.number}</span>
          </div>
        )}
      </div>
      <div className="p-3 space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="font-mono text-sm text-white">
            #{listing.number}
          </span>
          <span
            className="text-[10px] uppercase tracking-widest text-[#FFD700] font-body"
            data-testid={`listing-grade-${listing.id}`}
          >
            {listing.grade}
          </span>
        </div>
        <p
          className="font-display text-lg font-semibold text-[#E5B83A]"
          data-testid={`listing-price-${listing.id}`}
        >
          {listing.price} FT
        </p>
        <div className="flex items-center justify-between text-[11px] text-[#A1A1AA] font-body">
          <span>{listing.seller_display_name || "—"}</span>
          {distance ? (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {distance}
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}
